import { convertQueryObjToString } from "utils";
import parentApi from "store/parentApi";

const unwrap = (response) => response?.data;

const SocialApis = parentApi.injectEndpoints({
  endpoints: (build) => ({
    /* ----------------------------- Profile ----------------------------- */
    getSocialProfile: build.query({
      query: (userCode) => `/social/profile/${userCode}`,
      transformResponse: unwrap,
      providesTags: (result, error, userCode) => [
        { type: "socialProfile", id: userCode },
        "socialProfile",
      ],
    }),
    getUserComparison: build.query({
      query: (userCode) => `/social/compare/${userCode}`,
      transformResponse: unwrap,
      providesTags: (result, error, userCode) => [
        { type: "userComparison", id: userCode },
      ],
    }),
    getUserActivity: build.query({
      query: ({ userCode, before }) =>
        `/social/profile/${userCode}/activity?${convertQueryObjToString({
          before,
          limit: 20,
        })}`,
      transformResponse: unwrap,
      providesTags: (result, error, arg) => [
        { type: "userActivity", id: arg?.userCode },
      ],
    }),
    getProfileSharedContent: build.query({
      query: ({ userCode, before } = {}) =>
        `/social/profile/${userCode}/shared-content?${convertQueryObjToString({
          before,
          limit: 6,
        })}`,
      transformResponse: unwrap,
      providesTags: (result, error, arg) => [
        { type: "profileSharedContent", id: arg?.userCode },
        "profileSharedContent",
      ],
    }),
    getRelationship: build.query({
      query: (userCode) => `/social/relationship/${userCode}`,
      transformResponse: unwrap,
      providesTags: (result, error, userCode) => [
        { type: "relationship", id: userCode },
      ],
    }),

    /* -------------------------- Connections ---------------------------- */
    getFollowers: build.query({
      query: ({ userCode, q, page = 1 }) =>
        `/social/${userCode}/followers?${convertQueryObjToString({
          q,
          page,
          limit: 20,
        })}`,
      transformResponse: unwrap,
      providesTags: (result, error, arg) => [
        { type: "followers", id: arg?.userCode },
        "followers",
      ],
    }),
    getFollowing: build.query({
      query: ({ userCode, q, page = 1 }) =>
        `/social/${userCode}/following?${convertQueryObjToString({
          q,
          page,
          limit: 20,
        })}`,
      transformResponse: unwrap,
      providesTags: (result, error, arg) => [
        { type: "following", id: arg?.userCode },
        "following",
      ],
    }),
    getFollowRequests: build.query({
      query: () => `/social/follow/requests`,
      transformResponse: unwrap,
      providesTags: ["followRequests"],
    }),

    /* ---------------------------- Follow ------------------------------- */
    followUser: build.mutation({
      query: (target_user_code) => ({
        url: `/social/follow`,
        method: "POST",
        body: { target_user_code },
      }),
      transformResponse: unwrap,
      invalidatesTags: (result, error, target_user_code) => [
        { type: "relationship", id: target_user_code },
        { type: "socialProfile", id: target_user_code },
        { type: "followers", id: target_user_code },
        "followers",
        "following",
        "socialProfile",
        "people",
        "leaderboard",
      ],
    }),
    unfollowUser: build.mutation({
      query: (target_user_code) => ({
        url: `/social/unfollow`,
        method: "POST",
        body: { target_user_code },
      }),
      transformResponse: unwrap,
      invalidatesTags: (result, error, target_user_code) => [
        { type: "relationship", id: target_user_code },
        { type: "socialProfile", id: target_user_code },
        { type: "followers", id: target_user_code },
        "followers",
        "following",
        "socialProfile",
        "people",
        "feed",
        "leaderboard",
      ],
    }),
    acceptFollowRequest: build.mutation({
      query: (requester_user_code) => ({
        url: `/social/follow/accept`,
        method: "POST",
        body: { requester_user_code },
      }),
      transformResponse: unwrap,
      invalidatesTags: ["followRequests", "notifications", "feed", "followers", "following", "socialProfile", "leaderboard"],
    }),
    rejectFollowRequest: build.mutation({
      query: (requester_user_code) => ({
        url: `/social/follow/reject`,
        method: "POST",
        body: { requester_user_code },
      }),
      transformResponse: unwrap,
      invalidatesTags: ["followRequests", "notifications", "followers", "following", "socialProfile"],
    }),

    /* --------------------------- Discovery ----------------------------- */
    searchPeople: build.query({
      query: ({ q, role, educationLevel, specialty, sort, page = 1 } = {}) =>
        `/social/people?${convertQueryObjToString({
          q,
          role,
          education_level: educationLevel,
          specialty,
          sort,
          page,
          limit: 20,
        })}`,
      transformResponse: unwrap,
      providesTags: ["people"],
    }),

    /* ------------------------------ Feed ------------------------------- */
    getFeed: build.query({
      query: ({ type, before } = {}) =>
        `/social/feed?${convertQueryObjToString({ type, before, limit: 20 })}`,
      transformResponse: unwrap,
      providesTags: ["feed"],
    }),

    /* ----------------------------- Sharing ----------------------------- */
    shareContent: build.mutation({
      query: (body) => ({
        url: `/social/share`,
        method: "POST",
        body,
      }),
      transformResponse: unwrap,
      invalidatesTags: ["feed", "profileSharedContent"],
    }),

    /* -------------------------- Notifications -------------------------- */
    getNotifications: build.query({
      query: ({ before, limit = 20 } = {}) =>
        `/social/notifications?${convertQueryObjToString({
          before,
          limit,
        })}`,
      transformResponse: unwrap,
      providesTags: ["notifications"],
    }),
    getUnreadCount: build.query({
      query: () => `/social/notifications/unread-count`,
      transformResponse: unwrap,
      providesTags: ["notifications"],
    }),
    markNotificationsRead: build.mutation({
      query: (body) => ({
        url: `/social/notifications/read`,
        method: "POST",
        body: body || { all: true },
      }),
      transformResponse: unwrap,
      invalidatesTags: ["notifications"],
    }),

    /* --------------------------- Leaderboard --------------------------- */
    getLeaderboard: build.query({
      query: ({ scope = "global", metric = "questions", timeframe = "week" } = {}) =>
        `/social/leaderboard?${convertQueryObjToString({
          scope,
          metric,
          timeframe,
        })}`,
      transformResponse: unwrap,
      providesTags: ["leaderboard"],
    }),

    /* -------------------------- Preferences ---------------------------- */
    getSocialPrefs: build.query({
      query: () => `/social/prefs`,
      transformResponse: unwrap,
      providesTags: ["socialPrefs"],
    }),
    updateSocialPrefs: build.mutation({
      query: (body) => ({
        url: `/social/prefs`,
        method: "POST",
        body,
      }),
      transformResponse: unwrap,
      invalidatesTags: ["socialPrefs"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetSocialProfileQuery,
  useGetUserComparisonQuery,
  useGetUserActivityQuery,
  useLazyGetUserActivityQuery,
  useGetProfileSharedContentQuery,
  useGetRelationshipQuery,
  useGetFollowersQuery,
  useLazyGetFollowersQuery,
  useGetFollowingQuery,
  useLazyGetFollowingQuery,
  useGetFollowRequestsQuery,
  useFollowUserMutation,
  useUnfollowUserMutation,
  useAcceptFollowRequestMutation,
  useRejectFollowRequestMutation,
  useSearchPeopleQuery,
  useLazySearchPeopleQuery,
  useGetFeedQuery,
  useLazyGetFeedQuery,
  useShareContentMutation,
  useGetNotificationsQuery,
  useLazyGetNotificationsQuery,
  useGetUnreadCountQuery,
  useMarkNotificationsReadMutation,
  useGetLeaderboardQuery,
  useGetSocialPrefsQuery,
  useUpdateSocialPrefsMutation,
} = SocialApis;

export default SocialApis;
